import React, { useEffect, useState } from "react";
import { LoginForm, FormData } from "./FormComponent/FormComponent";
import {
    ChosenRole,
    RoleChoices,
} from "./RoleChoiceComponent/RoleChoiceComponent";
import { RolesResponse } from "../../../core/ServerApiLayer/VOne/auth/login";
import {
    LoginResponse,
    LoginUserType,
} from "../../../core/ServerApiLayer/VOne/auth/login";
import {
    UpdateSessionRoleSchema,
    UpdateRoleType,
} from "../../../core/ServerApiLayer/VOne/auth/updateRole";
import {
    getJwt, getUserSessionData,
    SetFromApiDataType
} from "../../../core/LocalStorage/AuthSession/core";
// @ts-ignore
import myImage from "../../../assets/outside.jpg";
// @ts-ignore
import logoImage from "../../../assets/clinical_metrics.png";
import divinaLogoImage from "../../../assets/divina.svg";
import { useNavigate } from "react-router-dom";
import { SupportedLanguage } from "../../../core/translations/supportedLanguages";
import { LoginPageTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { useLanguage } from "../../../core/translations/LanguageContext";
import {
    Container,
    DivArea,
    Heading,
    LeftPanel,
    LogoImage,
    RightPanel,
    SectionWithZeroMargin,
} from "./LoginPage.styles";
import DFNLoginButton from "./DFN/DFNButton";
import AgreementModal from "../../../components/core/modal/agreement-modal/AgreementModal";
import {
    AgreementResponse,
} from "../../../core/ServerApiLayer/VOne/auth/getAgreements";
import EnterLicenseModal from "../../../components/core/modal/enter-license-modal/EnterLicenseModal";

export interface LoginPageProps {
    setSessionData: SetFromApiDataType;
    loginApiCall: LoginUserType;
    updateRoleApiCall: UpdateRoleType;
    getAgreements: any;
    dataAgree: any;
    checkLicense: any;
    addLicense: any;
}

const TRANSLATIONS: Record<SupportedLanguage, LoginPageTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

const LoginPage: React.FC<LoginPageProps> = ({
                                                 setSessionData,
                                                 loginApiCall,
                                                 updateRoleApiCall,
                                                 getAgreements,
                                                 dataAgree,
                                                 checkLicense,
                                                 addLicense
                                             }) => {
    const navigate = useNavigate();

    const [roleChoices, setRoleChoices] = useState<RolesResponse>({
        institution_roles: [],
        department_roles: [],
    });

    useEffect(()=>{
        const cookiesAccepted = localStorage.getItem("cookiesAccepted");
        if(cookiesAccepted !== "true"){
            navigate("/?cookie-policy");
            return;
        }
    },[])

    const [login, setLogin] = useState<boolean>(true);
    const [showAgreementModal, setShowAgreementModal] = useState<boolean>(false);
    const [showEnterLicenseModal, setShowEnterLicenseModal] = useState<boolean>(false);
    // Store the login response to avoid duplicate login calls
    const [storedLoginResponse, setStoredLoginResponse] = useState<LoginResponse | null>(null);

    const [formStatus, setFormStatus] = useState<FormData>({
        email: "",
        password: "",
        success: false,
        failure: false,
        error: "",
        loading: false,
    });

    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];

    const handleFormSubmit = async (userData: FormData) => {
        setFormStatus({ ...userData, loading: true });

        const response = await loginApiCall(userData.email, userData.password);
        if (response.status === 200 && response.data) {
            const loginResponse: LoginResponse = response.data;

            // Store the response to avoid calling login again
            setStoredLoginResponse(loginResponse);

            setSessionData(
                loginResponse.user_session,
                loginResponse.jwt,
                loginResponse.refresh_token
            );

            const jwt = loginResponse.jwt;

            try {
                const licenseResponse = await checkLicense(jwt);

                if (licenseResponse.status !== 200) {
                    // No valid license, show license entry modal
                    setShowEnterLicenseModal(true);
                    return; // Stop here until license is added
                }

                // License is valid, check for agreements
                const agreementsResponse = await getAgreements(jwt);
                let needsAgreement = false;

                if (agreementsResponse.status === 200 && agreementsResponse.data) {
                    const agreements: AgreementResponse = agreementsResponse.data;

                    needsAgreement = !agreements.some(
                        (agreement) =>
                            agreement.agreement_type_name === "general data agreement" &&
                            agreement.agreement_type_version === 1
                    );
                }

                if (needsAgreement) {
                    // User needs to sign an agreement
                    setShowAgreementModal(true);
                    return;
                }

                // No agreements needed, proceed with checking roles
                const userSessionRoles = {
                    institution_roles: loginResponse.user_session.role
                        ? [{ role_name: loginResponse.user_session.role, entity_id: loginResponse.user_session.entity_id }]
                        : [],
                    department_roles: loginResponse.user_session.role
                        ? [{ role_name: loginResponse.user_session.role, entity_id: loginResponse.user_session.entity_id }]
                        : []
                };

                const roles = loginResponse.roles || userSessionRoles;

                if (!roles.institution_roles.length && !roles.department_roles.length && !loginResponse.user_session.role) {
                    console.warn("No roles found in login response");
                    setFormStatus({
                        ...userData,
                        success: false,
                        failure: true,
                        error: "Account setup incomplete. Please contact support.",
                        loading: false,
                    });
                    return;
                } else {
                    setRoleChoices({
                        institution_roles: roles.institution_roles,
                        department_roles: roles.department_roles,
                    });

                    if (roles.institution_roles.length > 1 || roles.department_roles.length > 1) {
                        setLogin(false);
                    } else {
                        setFormStatus({
                            ...userData,
                            success: true,  // Ensure success is set correctly
                            failure: false,
                            error: "",
                            loading: false,
                        });
                        setLogin(true);
                    }
                }
            } catch (error) {
                //@ts-ignore
                if (error.response?.status === 404) {
                    console.info("No license found — displaying license entry modal.");
                    setShowEnterLicenseModal(true);
                    return;
                }
                console.error("Unexpected error during license check:", error);
                setFormStatus({
                    ...userData,
                    success: false,
                    failure: true,
                    error: "Error checking license. Please try again.",
                    loading: false,
                });
            }
        } else {
            setFormStatus({
                ...userData,
                success: false,
                failure: true,
                error: response.error ?? "",
                loading: false,
            });
        }
    };

    const handleRoleChoiceSubmit = async (data: ChosenRole) => {
        if (!data.role) {
            return null;
        }

        // Get refresh token id from user session data
        let userSessionData = getUserSessionData();
        if (!userSessionData) {
            return null;
        }
        let refresh_token = userSessionData?.userSession.refreshTokenId;

        let schema: UpdateSessionRoleSchema;
        if ("department_id" in data.role) {
            schema = {
                role: data.role.role_name,
                entity_type: "department",
                entity_id: data.role.department_id,
                refresh_token_id: refresh_token,
            };
        } else if ("institution_id" in data.role) {
            schema = {
                role: data.role.role_name,
                entity_type: "institution",
                entity_id: data.role.institution_id,
                refresh_token_id: refresh_token,
            };
        } else {
            return null;
        }

        const response = await updateRoleApiCall(schema);
        if (response.status === 200 && response.data) {
            setSessionData(
                response.data.user_session,
                response.data.jwt,
                response.data.refresh_token
            );
            setFormStatus({
                ...formStatus,
                success: true,
                failure: false,
                error: "",
                loading: false,
            });
            setLogin(true);
            return null;
        } else {
            setFormStatus({
                ...formStatus,
                success: false,
                failure: true,
                error: response.error ?? "",
            });
            return null;
        }
    };

    const handleLicenseSubmit = async (licenseKey: string) => {
        const jwt = getJwt();
        const response = await addLicense(jwt, { license_key: licenseKey });

        if (response.status === 201) {
            const licenseCheck = await checkLicense(jwt);
            if (licenseCheck.status === 200) {
                setShowEnterLicenseModal(false);

                // Continue the login flow with stored login response
                console.log("Checking license after adding license key: ", storedLoginResponse);
                if (storedLoginResponse) {
                    const activeLoginResponse = storedLoginResponse;

                    // Check if user needs to sign an agreement
                    const agreementsResponse = await getAgreements(jwt);
                    let needsAgreement = true;  // Assume agreement is required unless proven otherwise

                    if (agreementsResponse.status === 200 && agreementsResponse.data) {
                        const agreements: AgreementResponse = agreementsResponse.data;

                        // Require agreement if no general data agreement version 1 or higher is found
                        const hasValidAgreement = agreements.some(
                            (agreement) =>
                                agreement.agreement_type_name === "general data agreement" &&
                                agreement.agreement_type_version >= 1
                        );

                        // If a valid agreement is found, no agreement is needed
                        if (hasValidAgreement) {
                            needsAgreement = false;
                        }
                    }

                    if (needsAgreement) {
                        setShowAgreementModal(true);
                        return;
                    }

                    // if role not empty
                    if (activeLoginResponse.user_session.role) {
                        setLogin(true);
                        setFormStatus({
                            ...formStatus,
                            success: true,
                            failure: false,
                            error: "",
                            loading: false,
                        });
                        return;
                    }

                    if (!activeLoginResponse.roles) {
                        console.warn("No roles found in login response after license check");
                        setFormStatus({
                            ...formStatus,
                            success: false,
                            failure: true,
                            error: "Account setup incomplete. Please contact support.",
                            loading: false,
                        });
                        return;
                    } else {
                        setRoleChoices({
                            institution_roles: activeLoginResponse.roles.institution_roles,
                            department_roles: activeLoginResponse.roles.department_roles,
                        });

                        if (
                            activeLoginResponse.roles.institution_roles.length > 1 ||
                            activeLoginResponse.roles.department_roles.length > 1
                        ) {
                            setLogin(false);
                        } else {
                            setLogin(true);
                        }
                    }
                }

            }
        } else {
            setFormStatus({
                ...formStatus,
                error: response.message || "An error occurred while adding the license."
            });
        }
    };

    const onSubmitAgree = async () => {
        const jwt = getJwt();
        const agreeResponse = await dataAgree(jwt);

        if (agreeResponse.status !== 200) {
            console.error("Failed to submit agreement");
            setFormStatus({
                ...formStatus,
                success: false,
                failure: true,
                error: "Failed to submit agreement. Please try again.",
                loading: false,
            });
            return;
        }

        setShowAgreementModal(false);
        // If role not empty
        if (storedLoginResponse?.user_session.role) {
            setLogin(true);
            setFormStatus({
                ...formStatus,
                success: true,
                failure: false,
                error: "",
                loading: false,
            });
            return;
        }

        // After agreement, continue with login flow
        if (storedLoginResponse && storedLoginResponse.roles) {
            setRoleChoices({
                institution_roles: storedLoginResponse.roles.institution_roles,
                department_roles: storedLoginResponse.roles.department_roles,
            });

            // If multiple roles exist, prompt for role selection
            if (
                storedLoginResponse.roles.institution_roles.length > 1 ||
                storedLoginResponse.roles.department_roles.length > 1
            ) {
                setLogin(false);
            } else {
                setLogin(true);

                console.log("Agreement accepted, logging in with stored response");
                window.location.reload();

                setFormStatus({
                    ...formStatus,
                    success: true,
                    failure: false,
                    error: "",
                    loading: false,
                });
            }
        } else {
            setFormStatus({
                ...formStatus,
                success: true,
                failure: false,
                error: "",
                loading: false,
            });
            setLogin(true);
        }
    };

    return (
        <Container backgroundImage={myImage}>
            {showAgreementModal && (
                <AgreementModal onSubmitAgree={() => onSubmitAgree()} />
            )}
            {showEnterLicenseModal && (
                <EnterLicenseModal
                    onEnterLicenseClose={() => setShowEnterLicenseModal(false)}
                    addLicense={handleLicenseSubmit}
                    error={formStatus.error}
                />
            )}
            <LeftPanel>
                <DivArea>
                    <LogoImage
                        src={window.location.hostname === "divinagame.de" ? divinaLogoImage :logoImage}
                        alt="description"
                        onClick={() => navigate("/")}
                    />
                    {!formStatus.success && !formStatus.loading && (
                        <Heading>{translations.heading}</Heading>
                    )}
                </DivArea>

                {/* Add DFN Login Button here */}
                {window.location.hostname === "divinagame.de" && <DFNLoginButton />}

                {!login && (
                    <SectionWithZeroMargin>
                        <RoleChoices
                            onSubmit={handleRoleChoiceSubmit}
                            initialRoleChoicesData={roleChoices}
                        />
                    </SectionWithZeroMargin>
                )}
                {login && (
                    <SectionWithZeroMargin>
                        <LoginForm
                            onSubmit={handleFormSubmit}
                            initialFormData={formStatus}
                            showAgreementModal={showAgreementModal}
                        />
                    </SectionWithZeroMargin>
                )}
            </LeftPanel>
            <RightPanel>{/* */}</RightPanel>
        </Container>
    );
};

export default LoginPage;